import React, { FC, useEffect, useRef, useState } from 'react';
import { Box, Grid, Link, Skeleton, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import dayjs from 'dayjs';
import Constants from '../../store/constants';
import { EventInformationType } from '../../core/models/events';
import { useRoutesHelper } from '../../helpers/routesHelper';
import { AppTheme } from '../../settings/appTheme';

const useStyles = makeStyles<void, 'gridTileImage' | 'gradientImg' | 'titleEvent'>()((_theme, _params, classes) => ({
  box: {
    overflowX: 'scroll',
    overflowY: 'hidden',
    '&::-webkit-scrollbar': {
      height: `${AppTheme.scrollbarHeight}`,
    },
    marginBottom: 50,
  },
  gridList: {
    flexWrap: 'nowrap',
    // Promote the list into his own layer on Chrome. This cost memory but helps keeping high FPS.
    transform: 'translateZ(0)',
    marginBottom: '7px',
    marginTop: '7px',
  },
  gridTile: {
    height: '88px',
  },
  gridContainer: {
    width: '266px',
    cursor: 'pointer',
  },
  gridTileImage: {
    height: '88px',
    width: '88px',
    objectFit: 'cover',
    borderRadius: '10px',
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.4)',
  },
  skeletonImage: {
    height: '88px',
    width: '88px',
    borderRadius: '10px',
  },
  skeletonText: {
    height: 20,
    marginTop: 2,
    marginLeft: 5,
  },
  textEvent: {
    fontSize: '14px',
    lineHeight: '14px',
    color: '#6B6B6B',
  },
  linkStyle: {
    '&:hover': {
      [`& .${classes.gridTileImage}, .${classes.gradientImg}, .${classes.titleEvent}`]: {
        filter: 'brightness(60%)',
      },
    },
  },
  gridImg: {
    position: 'relative',
  },
  gradientImg: {
    position: 'absolute',
    width: '88px',
    height: '32px',
    left: '0px',
    bottom: '5px',
    background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000000 100%)',
    opacity: 0.7,
    borderRadius: '0px 0px 8px 8px',
  },
  titleEvent: {
    fontSize: '16px',
    lineHeight: '16px',
    marginBottom: '12px',
  },
  linesContainer: {
    marginBottom: 29,
  },
}));

const LOAD_MORE_THRESHOLD = 400;

const distributeIntoRows = (flatEvents: EventInformationType[], rowCount: number): EventInformationType[][] => {
  const result: EventInformationType[][] = [];
  for (let row = 0; row < rowCount; row++) {
    const items: EventInformationType[] = [];
    for (let i = 0; i < flatEvents.length; i += rowCount) {
      const event = flatEvents[row + i];
      if (event) items.push(event);
    }
    result.push(items);
  }
  return result;
};

export const DatepickerCarousel: FC<DatepickerEventCarouselType> = ({
  load,
  reloadDependencyValue,
  defaultRowsCount,
  pageSize,
}) => {
  const [rowsCount, setRowsCount] = useState(defaultRowsCount);
  const [events, setEvents] = useState<EventInformationType[][]>([]);
  const [isLoading, setLoading] = useState(false);
  const [isLoadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const flatEventsRef = useRef<EventInformationType[]>([]);
  const rowsCountRef = useRef(defaultRowsCount);
  const loadingMoreRef = useRef(false);
  const boxRef = useRef<HTMLElement>(null);

  const { classes } = useStyles();
  const { handleEventLink, getEventRoute } = useRoutesHelper();
  const { selectedDate, cityId, selectedGenreId } = reloadDependencyValue;

  const effectivePageSize = pageSize ?? 12;

  useEffect(() => {
    setLoading(true);
    setHasMore(true);
    flatEventsRef.current = [];

    load(0).then((result) => {
      const safe = result ?? [];
      const defaultColumnCount = 3;
      let internalRowCount = defaultRowsCount;

      if (safe.length > defaultColumnCount) {
        while (safe.length / internalRowCount < defaultColumnCount && internalRowCount > 2) {
          internalRowCount--;
        }
      }

      rowsCountRef.current = internalRowCount;
      setRowsCount(internalRowCount);
      flatEventsRef.current = safe;
      setEvents(distributeIntoRows(safe, internalRowCount));
      setHasMore(safe.length >= effectivePageSize);
      setLoading(false);
    });
  }, [selectedDate, cityId, selectedGenreId]);

  const loadMore = async () => {
    if (!hasMore || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);

    const currentSkip = flatEventsRef.current.length;
    const res = await load(currentSkip);
    const incoming = res ?? [];
    const newAll = [...flatEventsRef.current, ...incoming];
    flatEventsRef.current = newAll;
    setEvents(distributeIntoRows(newAll, rowsCountRef.current));
    setHasMore(incoming.length >= effectivePageSize);
    setLoadingMore(false);
    loadingMoreRef.current = false;
  };

  const handleScroll = () => {
    if (!hasMore || loadingMoreRef.current || isLoading || isLoadingMore) return;
    const el = boxRef.current;
    if (!el) return;
    if (el.scrollLeft + el.clientWidth >= el.scrollWidth - LOAD_MORE_THRESHOLD) {
      loadMore();
    }
  };

  const renderEvent = (item: EventInformationType) => (
    <Grid item key={item.slug} className={classes.gridTile}>
      <Link
        underline={'none'}
        className={classes.linkStyle}
        onClick={() => handleEventLink(item.domainTypes, getEventRoute(item.slug))}
      >
        <Grid container className={classes.gridContainer} direction="row" justifyContent="center" alignItems="center">
          <Grid item lg={4} xs={4} sm={4} md={4} xl={4} className={classes.gridImg}>
            <img src={item.posterUrl} alt={item.title} className={classes.gridTileImage} loading="lazy" />
            <div className={classes.gradientImg} />
          </Grid>
          <Grid item lg={8} xs={8} sm={8} md={8} xl={8} style={{ margin: 0 }}>
            <Grid container style={{ marginLeft: '10px' }}>
              <Grid item lg={12} xs={12} sm={12} md={12} xl={12}>
                <Typography className={classes.titleEvent} variant="subtitle2">
                  {item.title}
                </Typography>
              </Grid>
              <Grid item lg={12} xs={12} sm={12} md={12} xl={12}>
                <Typography variant="caption" className={classes.textEvent}>
                  {dayjs(item.eventDate).format(Constants.DateFormat)}
                </Typography>
              </Grid>
              <Grid item lg={12} xs={12} sm={12} md={12} xl={12}>
                <Typography variant="caption" className={classes.textEvent}>
                  {item.city}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Link>
    </Grid>
  );

  const renderSkeleton = (index: number) => (
    <Grid key={index} item className={classes.gridTile}>
      <Grid
        container
        spacing={1}
        direction="row"
        justifyContent="center"
        alignItems="center"
        style={{ minWidth: '266px' }}
      >
        <Grid item lg={4} xs={4} sm={4} md={4} xl={4}>
          <Skeleton animation="wave" variant="rectangular" className={classes.skeletonImage} />
        </Grid>
        <Grid item lg={8} xs={8} sm={8} md={8} xl={8}>
          <Skeleton animation="wave" variant="rectangular" width="75%" className={classes.skeletonText} />
          <Skeleton animation="wave" variant="rectangular" width="65%" className={classes.skeletonText} />
          <Skeleton animation="wave" variant="rectangular" width="55%" className={classes.skeletonText} />
        </Grid>
      </Grid>
    </Grid>
  );

  const renderLine = (items: EventInformationType[], lineIndex: number) => (
    <Grid container className={classes.gridList}>
      {!isLoading && items.length > 0 && items.map((item) => renderEvent(item))}
      {isLoading && Array.from(new Array(rowsCount)).map((_, index) => renderSkeleton(index))}
      {isLoadingMore && renderSkeleton(lineIndex + 1000)}
    </Grid>
  );

  return (
    <Box ref={boxRef} className={`carousel ${classes.box}`} onScroll={handleScroll}>
      <Grid container className={classes.linesContainer}>
        {events?.map((item, index) => (
          <Grid key={index} item>
            {renderLine(item, index)}
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

type DatepickerEventCarouselType = {
  reloadDependencyValue?: any;
  load: (skip: number) => Promise<EventInformationType[]>;
  defaultRowsCount: number;
  pageSize?: number;
};
